import { Link, useNavigate } from '@tanstack/react-router';
import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BOARD_COLUMN_CARD_LIMIT, type ApplicationStage, type PipelineBoardResponse, type PipelineOptions, type PipelineRow, type ScreeningGuard } from '@nest-recruit/shared';
import { Skeleton } from '../../../../components/Skeleton';
import { STAGE_TONES } from '../../../../components/status-tones';
import { anchorFrom } from '../../../../lib/anchored-panel';
import { formatNumber } from '../../../../lib/format';
import { useModule } from '../../../../lib/modules';
import { useNow } from '../../../../lib/use-now';
import type { usePipelineActions } from '../../hooks/use-pipeline-actions';
import { intentFor } from '../../move-intent';
import { useRowMoves } from '../RowMoves';
import { BoardCard } from './BoardCard';
import { BoardColumn, type DropState } from './BoardColumn';
import styles from './PipelineBoard.module.css';

/** A press becomes a drag after this many pixels — below it, it is a click. */
const DRAG_THRESHOLD_PX = 6;
/** The click a browser reports after a drop is not an "open this card". */
const CLICK_AFTER_DRAG_MS = 250;
const MINUTE_MS = 60_000;
const SKELETON_LANES = 4;

interface Drag {
  readonly row: PipelineRow;
  readonly x: number;
  readonly y: number;
  readonly over: ApplicationStage | null;
}

interface PipelineBoardProps {
  readonly jobId: string;
  readonly data: PipelineBoardResponse | undefined;
  readonly isLoading: boolean;
  readonly options: PipelineOptions | undefined;
  readonly actions: ReturnType<typeof usePipelineActions>;
  readonly stageLabel: (stage: ApplicationStage) => string;
}

/**
 * SCR-03 — one column per stage the job uses; drag a card to move it (adapted from
 * Tomo's `BoardView.tsx`: hand-rolled pointer events, a portalled ghost, the move shown
 * before the server agrees and held until the refetch lands). **A drag is a stage
 * transition** with exactly the button path's validation (F8.13): dropping on Rejected
 * asks for the reason, an HR Lead's skip asks why, and a column the card may not enter
 * says so while the card is over it.
 */
export function PipelineBoard({ jobId, data, isLoading, options, actions, stageLabel }: PipelineBoardProps): ReactNode {
  const navigate = useNavigate();
  const now = useNow(MINUTE_MS);
  const [collapsed, setCollapsed] = useState<ReadonlySet<ApplicationStage>>(new Set());
  const [drag, setDrag] = useState<Drag | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const draggedAt = useRef(0);
  const activeStages = data?.job.activeStages ?? [];
  const active = new Set(activeStages);
  const can = data?.can;
  const rowMoves = useRowMoves({ jobId, activeStages, can, options, actions, via: 'board' });
  const isScreeningGuardOn = useModule('screening_order');
  const guardFor = (row: PipelineRow): ScreeningGuard => ({ isOn: isScreeningGuardOn, managerResponse: row.managerResponse });

  if (!data) return isLoading ? <BoardSkeleton /> : null;
  const lanes: readonly ApplicationStage[] = [...data.columns.map((column) => column.stage), 'rejected'];
  const truncated = new Set(data.truncated);

  function laneAt(x: number, y: number): { stage: ApplicationStage; element: HTMLElement } | null {
    for (const element of wrapRef.current?.querySelectorAll<HTMLElement>('[data-lane]') ?? []) {
      const rect = element.getBoundingClientRect();
      const stage = lanes.find((lane) => lane === element.dataset.lane);
      if (stage && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return { stage, element };
    }
    return null;
  }

  function onDragStart(event: ReactPointerEvent<HTMLElement>, row: PipelineRow): void {
    if (can?.move !== true || event.button !== 0) return;
    // The card's own link and ⋯ button stay themselves.
    if (event.target instanceof Element && event.target.closest('a,button')) return;
    const startX = event.clientX;
    const startY = event.clientY;
    let isStarted = false;
    let over: { stage: ApplicationStage; element: HTMLElement } | null = null;

    const onMove = (move: PointerEvent): void => {
      if (!isStarted && Math.abs(move.clientX - startX) + Math.abs(move.clientY - startY) < DRAG_THRESHOLD_PX) return;
      isStarted = true;
      over = laneAt(move.clientX, move.clientY);
      setDrag({ row, x: move.clientX, y: move.clientY, over: over?.stage ?? null });
    };
    const finish = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey, true);
      setDrag(null);
    };
    const onUp = (): void => {
      finish();
      if (!isStarted) return;
      draggedAt.current = Date.now();
      if (over && over.stage !== row.stage) rowMoves.request(row, over.stage, anchorFrom(over.element));
    };
    // Esc puts the card back where it was, and nothing is sent.
    const onKey = (key: KeyboardEvent): void => {
      if (key.key !== 'Escape') return;
      key.stopPropagation();
      isStarted = false;
      finish();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey, true);
  }

  function dropFor(stage: ApplicationStage): DropState {
    if (!drag || stage === drag.row.stage) return { kind: 'idle' };
    const isOver = drag.over === stage;
    const intent = intentFor(drag.row.stage, stage, active, can?.skip === true, guardFor(drag.row));
    if (intent.kind === 'refused') return { kind: 'no', isOver, reason: intent.reason };
    if (intent.kind === 'reject') return { kind: 'ok', isOver, hint: 'Drop to reject — you will pick the reason.' };
    if (intent.kind === 'skip' || intent.kind === 'skip_screening') return { kind: 'ok', isOver, hint: 'A skip — you will say why.' };
    return { kind: 'ok', isOver, hint: null };
  }

  function toggle(stage: ApplicationStage): void {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  }

  function open(row: PipelineRow): void {
    if (Date.now() - draggedAt.current < CLICK_AFTER_DRAG_MS) return;
    void navigate({ to: '/applications/$applicationId', params: { applicationId: row.id } });
  }

  return (
    <>
      <div ref={wrapRef} className={[styles.board, drag ? styles.isDragging : ''].filter(Boolean).join(' ')} role="list" aria-label="Pipeline board">
        {data.columns.map((column) => {
          const cards = data.cards.filter((card) => card.stage === column.stage);
          return (
            <BoardColumn
              key={column.stage}
              lane={column.stage}
              tone={STAGE_TONES[column.stage]}
              label={stageLabel(column.stage)}
              count={column.count}
              shown={cards.length}
              isCollapsed={collapsed.has(column.stage)}
              onToggle={() => toggle(column.stage)}
              drop={dropFor(column.stage)}
              empty="Nobody here."
              footer={
                truncated.has(column.stage) ? (
                  <Link to="/jobs/$jobId/pipeline" params={{ jobId }} search={{ stage: column.stage }} className={styles.more}>
                    Best {BOARD_COLUMN_CARD_LIMIT} of {formatNumber(column.count)} — see them all in the list
                  </Link>
                ) : undefined
              }
            >
              {cards.map((card) => (
                <BoardCard
                  key={card.id}
                  row={card}
                  now={now}
                  isPending={actions.pending.has(card.id)}
                  isDragging={drag?.row.id === card.id}
                  onDragStart={onDragStart}
                  onOpen={open}
                  onMenu={(row, trigger) => rowMoves.openMenu(row, anchorFrom(trigger))}
                />
              ))}
            </BoardColumn>
          );
        })}
        <BoardColumn
          lane="rejected"
          tone={STAGE_TONES.rejected}
          label={stageLabel('rejected')}
          count={data.rejectedCount}
          shown={0}
          isCollapsed={false}
          onToggle={() => void navigate({ to: '/jobs/$jobId/pipeline', params: { jobId }, search: { stage: 'rejected' } })}
          drop={dropFor('rejected')}
          empty="Drop someone here to reject them. You pick the reason; they never see it."
          footer={
            <p className={styles.closed}>
              {data.closedCount > 0 ? `${formatNumber(data.closedCount)} more withdrew, joined, moved or closed. ` : ''}
              <Link to="/jobs/$jobId/pipeline" params={{ jobId }} search={{ stage: 'closed' }} className={styles.inline}>
                See them in the list
              </Link>
            </p>
          }
        />
      </div>

      {drag &&
        createPortal(
          <div className={styles.ghost} style={{ left: drag.x, top: drag.y }} aria-hidden>
            {drag.row.candidate.fullName}
          </div>,
          document.body,
        )}

      {rowMoves.popover}
    </>
  );
}

function BoardSkeleton(): ReactNode {
  return (
    <div className={styles.board} aria-hidden>
      {Array.from({ length: SKELETON_LANES }, (_, index) => (
        <div key={index} className={styles.skeletonLane}>
          <Skeleton shape="title" width="60%" />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      ))}
    </div>
  );
}
